import { useEffect, useState, useCallback, type ReactNode } from "react";
import { useLocation } from "react-router";
import {
  detectUserRegion,
  formatRegionTime,
  formatRegionDate,
  REGION_STORAGE_KEY,
  type UserRegionInfo,
} from "@/lib/userRegion";
import { UserRegionContext } from "@/contexts/userRegionContextDef";
import {
  CONSENT_STORAGE_KEY,
  grantedConsent,
  readStoredGoogleConsent,
  updateGoogleConsent,
} from "@/lib/googleTag";

export function UserRegionProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  const [regionInfo, setRegionInfo] = useState<UserRegionInfo>(() =>
    detectUserRegion(),
  );

  const [currentTime, setCurrentTime] = useState<string>(() =>
    formatRegionTime(new Date(), regionInfo.timeZone),
  );

  const [currentDate, setCurrentDate] = useState<string>(() =>
    formatRegionDate(new Date(), regionInfo.timeZone),
  );

  // Automatically enable analytics acceptance for all non-admin users
  useEffect(() => {
    if (isAdmin) return;

    const existing = readStoredGoogleConsent();
    if (!existing) {
      // Auto-grant analytics for public visitors
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(grantedConsent));
      updateGoogleConsent(grantedConsent);
      window.dispatchEvent(new CustomEvent("bloxida:consent-changed"));
    } else {
      updateGoogleConsent(existing);
    }
  }, [isAdmin]);

  // Keep live local AM/PM clock updated
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setCurrentTime(formatRegionTime(now, regionInfo.timeZone));
      setCurrentDate(formatRegionDate(now, regionInfo.timeZone));
    };

    updateClock();
    const interval = window.setInterval(updateClock, 1000);
    return () => window.clearInterval(interval);
  }, [regionInfo.timeZone]);

  const updateRegion = useCallback((info: Partial<UserRegionInfo>) => {
    setRegionInfo((prev) => {
      const next = { ...prev, ...info };
      try {
        localStorage.setItem(REGION_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Ignore storage errors
      }
      return next;
    });
  }, []);

  const requestPreciseLocation = useCallback(async () => {
    if (typeof window === "undefined" || !navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        updateRegion({
          latitude,
          longitude,
          isPrecise: true,
        });
      },
      (err) => {
        console.warn("Precise location unavailable:", err.message);
      },
      { timeout: 8000, maximumAge: 60000 },
    );
  }, [updateRegion]);

  return (
    <UserRegionContext
      value={{
        regionInfo,
        currentTime,
        currentDate,
        requestPreciseLocation,
        updateRegion,
      }}
    >
      {children}
    </UserRegionContext>
  );
}
